<template>
  <div class="panel cc-panel">
    <p class="panel-heading">
      {{ $t("i18n.chartTitles.ccEnergyPerTime") }}
    </p>
    <div class="panel-block">
      <div :id="this.idChartDiv" class="cc-chart"></div>
    </div>
  </div>
</template>
<script>
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting'
import ccColors from '@/components/ccColors'

export default {
  name: 'ccEnergyPerTime',
  props: {
    idChartDiv: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      root: null,
      chart: null,
      xAxis: null,
      yAxis: null,
      dateTimeName: 'smpDate'
    }
  },
  mounted () {
    this.buildChart()
  },
  methods: {
    buildChart: function () {
      am5.addLicense(this.$store.state.am5)
      const root = am5.Root.new(this.idChartDiv)

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
          interactive: true,
          paddingTop: 30
        })
      )

      // Create axes
      const xAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          baseInterval: { timeUnit: 'day', count: 1 },
          gridIntervals: [
            { timeUnit: 'day', count: 1 },
            { timeUnit: 'month', count: 1 }
          ],
          renderer: am5xy.AxisRendererX.new(root, {}),
          tooltip: am5.Tooltip.new(root, {}),
          startLocation: 0.5,
          endLocation: 0.5
        })
      )

      const yAxisLeft = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          numberFormat: '#',
          renderer: am5xy.AxisRendererY.new(root, {}),
          min: 0
        })
      )

      const yAxisRight = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          numberFormat: '#',
          renderer: am5xy.AxisRendererY.new(root, {}),
          strictMinMax: true,
          min: -50,
          max: 40
        })
      )

      // Add column series
      this.addEnergySeries(this.$t('i18n.global.compressor'), 'energyComp', ccColors.energyComp, root, chart, yAxisLeft, xAxis)
      this.addEnergySeries(this.$t('i18n.global.auxiliary'), 'energyAux', ccColors.energyAux, root, chart, yAxisLeft, xAxis)

      // Add line series
      this.addLineSeries(this.$t('i18n.global.outdoorTemperature'), 'outdoorTemperature', ccColors.outdoorTemperature, true, root, chart, yAxisRight, xAxis)
      this.addLineSeries(this.$t('i18n.global.signature'), 'signatureEnergyTot', ccColors.signature, false, root, chart, yAxisLeft, xAxis)

      // Add Legend
      const legend = chart.children.push(am5.Legend.new(root, {
        x: am5.p50,
        centerX: am5.p50,
        clickTarget: 'none' // Disable clickable legends
      }))
      legend.data.setAll(chart.series.values)

      // Add export menu
      am5exporting.Exporting.new(root, {
        menu: am5exporting.ExportingMenu.new(root, {})
      })

      // Add Labels to axises
      chart.children.unshift(am5.Label.new(root, {
        text: 'kWh',
        fontWeight: '500',
        y: am5.percent(-15),
        height: 0
      }))

      chart.children.unshift(am5.Label.new(root, {
        text: '°C',
        fontWeight: '500',
        textAlign: 'right',
        x: am5.percent(100),
        y: am5.percent(-15),
        height: 0
      }))

      // Make stuff animate on load
      chart.appear(1000, 100)
    },
    addEnergySeries: function (title, value, color, root, chart, yAxis, xAxis) {
      const energySeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: title,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: value,
          valueXField: this.dateTimeName,
          stacked: true
        })
      )

      const tooltip = energySeries.set('tooltip', am5.Tooltip.new(root, {
        pointerOrientation: 'horizontal'
      }))

      tooltip.label.setAll({
        text: '{name}: {valueY}'
      })

      energySeries.columns.template.setAll({
        stroke: color,
        fill: color
      })

      energySeries.data.processor = am5.DataProcessor.new(root, {
        dateFormat: 'yyyy-MM-dd',
        dateFields: [this.dateTimeName]
      })

      energySeries.data.setAll(this.data)

      return energySeries
    },
    addLineSeries: function (title, value, color, opposite, root, chart, yAxis, xAxis) {
      // add axis for this line series
      const renderer = yAxis.get('renderer')
      renderer.set('opposite', opposite)
      renderer.grid.template.set('forceHidden', true)

      const lineSeries = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: title,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: value,
          valueXField: this.dateTimeName,
          stroke: color,
          fill: color,
          connect: false
        })
      )

      const tooltip = lineSeries.set('tooltip', am5.Tooltip.new(root, {
        pointerOrientation: 'horizontal'
      }))

      tooltip.label.setAll({
        text: '{name}: {valueY}'
      })

      lineSeries.strokes.template.setAll({
        strokeWidth: 2
      })

      lineSeries.data.processor = am5.DataProcessor.new(root, {
        dateFormat: 'yyyy-MM-dd',
        dateFields: [this.dateTimeName]
      })

      lineSeries.data.setAll(this.data)

      return lineSeries
    }
  }
}
</script>
